import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { templateService } from '../services/db';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import PageTitle from '../components/PageTitle';
import './TemplatesPage.css';

function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Function to load templates with detailed logging
  const loadTemplates = useCallback(async () => {
    try {
      console.log("🔄 [TemplatesPage] Loading templates for user:", currentUser?.id);
      setIsLoading(true);
      const loadedTemplates = await templateService.getAll(currentUser.id);
      
      // Log detailed information about each template's thumbnail
      console.log("✅ [TemplatesPage] Loaded templates:", loadedTemplates.length);
      loadedTemplates.forEach(template => {
        console.log(`📁 [TemplatesPage] Template ${template.id} (${template.name}):`, {
          hasThumbnail: !!template.thumbnail,
          thumbnailPath: template.thumbnailPath || "Not set",
          thumbnailUrl: template.thumbnail ? "Set" : "Not set",
          usesNewPathFormat: template.thumbnailPath?.includes(`templates/${template.id}/thumbnail.png`) || false
        });
      });
      
      // Check if user is admin
      setIsAdmin(currentUser.isAdmin === true);
      
      setTemplates(loadedTemplates);
      return loadedTemplates;
    } catch (error) {
      console.error("❌ [TemplatesPage] Error loading templates:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Load templates on mount
  useEffect(() => {
    if (currentUser) {
      loadTemplates();
    }
  }, [currentUser, loadTemplates]);

  // Function to force reload templates and clear image caches
  const forceReloadTemplates = async () => {
    try {
      console.log("🔄 [TemplatesPage] Force reloading templates...");
      setIsRefreshing(true);
      
      // Load fresh templates
      const freshTemplates = await loadTemplates();
      console.log("✅ [TemplatesPage] Templates reloaded:", freshTemplates.length);
      
      // Clear image caches for thumbnails
      console.log("🧹 [TemplatesPage] Clearing thumbnail caches...");
      
      // Find all thumbnail images
      const thumbnailImages = document.querySelectorAll('.template-thumbnail img');
      console.log(`🔍 [TemplatesPage] Found ${thumbnailImages.length} thumbnail images to refresh`);
      
      // For each image, add a cache-busting parameter to the src
      thumbnailImages.forEach((img, index) => {
        if (img.src) {
          const originalSrc = img.src;
          
          // Generate timestamp for cache busting
          const timestamp = new Date().getTime();
          
          console.log(`🔄 [TemplatesPage] Refreshing thumbnail ${index + 1}/${thumbnailImages.length}`);
          
          // Force reload by removing and recreating the image element
          const parentElement = img.parentElement;
          if (parentElement) {
            // Create new image element
            const newImg = document.createElement('img');
            
            // Copy attributes
            Array.from(img.attributes).forEach(attr => {
              if (attr.name !== 'src') { // Skip src as we'll set it with cache busting
                newImg.setAttribute(attr.name, attr.value);
              }
            });
            
            // Set event handlers
            newImg.onload = () => console.log(`✅ [TemplatesPage] Thumbnail ${index + 1} reloaded successfully`);
            newImg.onerror = (e) => {
              console.error(`❌ [TemplatesPage] Error reloading thumbnail ${index + 1}:`, e);
              newImg.style.display = 'none';
              newImg.parentNode.classList.add('thumbnail-error');
            };
            
            // Add cache busting parameter
            let newSrc;
            if (originalSrc.includes('?')) {
              // URL already has parameters, append timestamp
              newSrc = `${originalSrc.split('?')[0]}?_cache=${timestamp}`;
            } else {
              // URL has no parameters yet, add timestamp
              newSrc = `${originalSrc}?_cache=${timestamp}`;
            }
            
            // Set new src with cache busting and replace the old image
            newImg.src = newSrc;
            parentElement.replaceChild(newImg, img);
            
            console.log(`🔄 [TemplatesPage] Replaced thumbnail ${index + 1} with cache-busted version`);
          } else {
            // Fallback method: just update the src
            img.src = '';  // Clear src first
            setTimeout(() => {
              // Add cache busting parameter
              let newSrc;
              if (originalSrc.includes('?')) {
                // URL already has parameters, replace them with new timestamp
                newSrc = `${originalSrc.split('?')[0]}?_cache=${timestamp}`;
              } else {
                // URL has no parameters yet, add timestamp
                newSrc = `${originalSrc}?_cache=${timestamp}`;
              }
              img.src = newSrc;
              console.log(`🔄 [TemplatesPage] Updated src for thumbnail ${index + 1} with cache-busting`);
            }, 50);
          }
        }
      });
      
      // Clear browser cache for storage URLs
      console.log("🧹 [TemplatesPage] Attempting to clear browser cache for Storage URLs");
      
      // Force service worker refresh if available
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        try {
          console.log("🔄 [TemplatesPage] Updating service worker");
          await navigator.serviceWorker.getRegistrations().then(registrations => {
            registrations.forEach(registration => registration.update());
          });
        } catch (swError) {
          console.warn("⚠️ [TemplatesPage] Service worker update error:", swError);
        }
      }
      
      console.log("✅ [TemplatesPage] Template refresh completed");
    } catch (error) {
      console.error("❌ [TemplatesPage] Error during template refresh:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeleteTemplate = (template) => {
    setTemplateToDelete(template);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    try {
      await templateService.delete(templateToDelete);
      setTemplates(templates.filter(t => t.id !== templateToDelete));
      setTemplateToDelete(null);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template: " + error.message);
    }
  };

  const cancelDeleteTemplate = () => {
    setTemplateToDelete(null);
    setIsDeleteDialogOpen(false);
  };

  // Toggle publish status of a template (admin only)
  const handleTogglePublish = async (id, currentStatus, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAdmin) {
      alert("Only administrators can publish templates");
      return;
    }
    
    try {
      await templateService.publishTemplate(id, !currentStatus);
      
      // Update local state
      setTemplates(templates.map(template => 
        template.id === id
          ? { ...template, isPublished: !currentStatus }
          : template
      ));
    } catch (err) {
      console.error("Error toggling template publish status:", err);
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="templates-page">
      <PageTitle title="Templates" />
      <Header />
      
      <div className="templates-container">
        <div className="templates-header">
          <h1>Templates</h1>
          <div className="templates-actions">
            <button 
              className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
              onClick={forceReloadTemplates}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Force Refresh'}
            </button>
            <Link to="/editor" className="create-template-btn">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Create Template
            </Link>
          </div>
        </div>
        
        {isLoading ? (
          <div className="loading-spinner">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            <p>You don't have any templates yet.</p>
            <Link to="/editor" className="create-first-template-btn">Create your first template</Link>
          </div>
        ) : (
          <div className="templates-grid">
            {templates.map(template => (
              <div key={template.id} className={`template-card ${template.isAdminTemplate ? 'admin-template' : ''} ${template.isPublished ? 'published-template' : ''}`}>
                <Link to={`/editor/${template.id}`} className="template-link">
                  <div className="template-thumbnail">
                    {template.thumbnail ? (
                      <>
                        <div className="template-thumbnail-wrapper">
                          <img 
                            key={template.id}
                            src={template.thumbnail} 
                            alt={template.name}
                            className={`template-thumbnail-image ${template.thumbnailPath ? 'has-path' : 'no-path'}`}
                            loading="lazy"
                            onLoad={() => console.log(`Thumbnail for ${template.name} loaded successfully`)}
                            onError={(e) => {
                              console.error(`Error loading thumbnail for ${template.name}:`, e);
                              // Fallback to placeholder on error
                              e.target.style.display = 'none';
                              e.target.parentNode.classList.add('thumbnail-error');
                            }}
                          />
                        </div>
                        {/* Debug info for thumbnail */}
                        {typeof template.thumbnail === 'string' && template.thumbnail.length > 100 ? 
                          <div style={{display: 'none'}}>
                            {console.log(`Valid thumbnail for ${template.name}, ${template.thumbnail.substring(0, 30)}...`)}
                          </div> : 
                          <div style={{display: 'none'}}>
                            {console.log(`Invalid thumbnail for ${template.name}:`, typeof template.thumbnail, template.thumbnail)}
                          </div>
                        }
                      </>
                    ) : (
                      <div className="placeholder-thumbnail">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM5 19V5H19V19H5Z" stroke="#AAAAAA" strokeWidth="1.5" />
                          <path d="M14.5 11L11 15.5L8.5 12.5L5 17H19L14.5 11Z" stroke="#AAAAAA" strokeWidth="1.5" />
                        </svg>
                      </div>
                    )}
                    
                    {/* Show status badge for published templates */}
                    {template.isPublished && (
                      <div className="template-badge published-badge" title="This template is published globally">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                        </svg>
                        Published
                      </div>
                    )}
                    
                    {/* Show indicator for admin templates that user didn't create */}
                    {template.isAdminTemplate && (
                      <div className="template-badge admin-badge" title="Created by administrator">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm3.5 12.09l-1.41 1.41L12 13.42 9.91 15.5 8.5 14.09 10.59 12 8.5 9.91 9.91 8.5 12 10.59l2.09-2.09 1.41 1.41L13.42 12l2.08 2.09z" fill="currentColor"/>
                        </svg>
                        Admin
                      </div>
                    )}
                  </div>
                  <h3>{template.name}</h3>
                  
                  <div className="template-actions">
                    {/* Only show delete button for user's own templates or if they're admin */}
                    {(!template.isAdminTemplate || isAdmin) && (
                      <button 
                        className="delete-template-btn" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteTemplate(template.id);
                        }}
                        title="Delete Template"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM19 4H15.5L14.5 3H9.5L8.5 4H5V6H19V4Z" fill="currentColor"/>
                        </svg>
                      </button>
                    )}
                    
                    {/* Show publish toggle for admin users */}
                    {isAdmin && (
                      <button 
                        className={`publish-template-btn ${template.isPublished ? 'published' : ''}`}
                        onClick={(e) => handleTogglePublish(template.id, template.isPublished, e)}
                        title={template.isPublished ? "Unpublish Template" : "Publish Template Globally"}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" fill="currentColor"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteTemplate}
        onCancel={cancelDeleteTemplate}
        type="delete"
      />
    </div>
  );
}

export default TemplatesPage; 